import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { type DeviceModelDefinition, fetchDeviceModels } from "./device-model-catalog";

type DeviceModelCatalogState = {
  error?: string;
  models: DeviceModelDefinition[];
  refresh: () => Promise<DeviceModelDefinition[]>;
  status: "error" | "loading" | "ready";
};

const DeviceModelCatalogContext = createContext<DeviceModelCatalogState | undefined>(undefined);

export function DeviceModelCatalogProvider({ children }: { children: ReactNode }) {
  const [models, setModels] = useState<DeviceModelDefinition[]>([]);
  const [status, setStatus] = useState<DeviceModelCatalogState["status"]>("loading");
  const [error, setError] = useState<string | undefined>();

  const refresh = useCallback(async () => {
    try {
      const nextModels = await fetchDeviceModels();
      setModels(nextModels);
      setStatus("ready");
      setError(undefined);
      return nextModels;
    } catch (refreshError) {
      setError(refreshError instanceof Error ? refreshError.message : "设备模型目录读取失败");
      setStatus("error");
      throw refreshError;
    }
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    void fetchDeviceModels(abortController.signal)
      .then((nextModels) => {
        setModels(nextModels);
        setStatus("ready");
      })
      .catch((loadError: unknown) => {
        if (abortController.signal.aborted) {
          return;
        }
        setError(loadError instanceof Error ? loadError.message : "设备模型目录读取失败");
        setStatus("error");
      });
    return () => abortController.abort();
  }, []);

  return (
    <DeviceModelCatalogContext.Provider value={{ error, models, refresh, status }}>
      {children}
    </DeviceModelCatalogContext.Provider>
  );
}

export function useDeviceModelCatalog() {
  const value = useContext(DeviceModelCatalogContext);
  if (!value) {
    throw new Error("useDeviceModelCatalog must be used inside DeviceModelCatalogProvider");
  }
  return value;
}
